import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';

/**
 * Custom size picker component for product cards
 * This avoids morphing issues by managing its own state
 */
export class SizePicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleSizeChange.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Handles size selection change
   * @param {Event} event - The change event
   */
  #handleSizeChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const selectedInput = event.target;
    const variantId = selectedInput.dataset.variantId;

    if (!variantId) return;

    // Update visual state immediately without waiting for fetch
    this.#updateVisualState(selectedInput);

    // Dispatch variant selected event
    this.dispatchEvent(
      new VariantSelectedEvent({ id: selectedInput.dataset.optionValueId ?? '' })
    );

    // Fetch variant data for price/image updates
    this.#fetchVariantData(variantId, selectedInput);
  }

  /**
   * Updates the visual checked state of size buttons
   * @param {HTMLInputElement} selectedInput - The selected input
   */
  #updateVisualState(selectedInput) {
    // Uncheck all radio inputs
    const allInputs = this.querySelectorAll('input[type="radio"]');
    allInputs.forEach((input) => {
      input.checked = false;
    });

    // Check the selected one
    selectedInput.checked = true;
  }

  /**
   * Fetches variant data and dispatches update event
   * @param {string} variantId - The variant ID
   * @param {HTMLInputElement} selectedInput - The selected input element
   */
  async #fetchVariantData(variantId, selectedInput) {
    // Abort previous request if still pending
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const productUrl = this.dataset.productUrl?.split('?')[0];
    const productId = this.dataset.productId ?? '';

    // Check global cache first (preloaded by product-card)
    /** @type {any} */
    const win = window;
    const cacheKey = `${productId}-${variantId}`;
    const cached = win?.ThemeVariantCache?.get(cacheKey);
    if (cached?.html && cached?.variant) {
      const html = new DOMParser().parseFromString(cached.html, 'text/html');
      const sourceId = selectedInput.dataset.optionValueId || '';
      this.dispatchEvent(
        new VariantUpdateEvent(cached.variant, sourceId, {
          html,
          productId,
          newProduct: undefined,
        })
      );
      return;
    }
    if (!productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'section-rendering-product-card');

    try {
      const response = await fetch(url.href, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) return;

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Get variant JSON from the response
      const variantScript = html.querySelector('variant-picker script[type="application/json"], swatches-variant-picker-component script[type="application/json"], size-picker script[type="application/json"]');
      if (!variantScript) return;

      const variant = JSON.parse(variantScript.textContent);
      const sourceId = selectedInput.dataset.optionValueId || '';

      // Extract and pre-load variant image
      let imageUrl = null;
      if (variant?.featured_media?.preview_image?.src) {
        imageUrl = variant.featured_media.preview_image.src;
        
        // Pre-load image into browser cache at carousel size
        const img = new Image();
        img.src = imageUrl + '&width=1200';
        
        // Also preload high-res version for zoom
        const highResImg = new Image();
        highResImg.src = imageUrl + '&width=3840';
      }

      // Dispatch VariantUpdateEvent for price/image updates
      // Note: We use the existing variant picker's HTML but don't morph ourselves
      this.dispatchEvent(
        new VariantUpdateEvent(variant, sourceId, {
          html,
          productId,
          newProduct: undefined,
        })
      );

      // Cache for subsequent toggles (with image URL)
      try {
        win?.ThemeVariantCache?.set(cacheKey, { 
          html: responseText, 
          variant,
          imageUrl: imageUrl || null
        });
      } catch (_) {}
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch variant data:', error);
      }
    }
  }
}

if (!customElements.get('size-picker')) {
  customElements.define('size-picker', SizePicker);
}

